/**
 * @file     env.ts
 * @purpose  Typed accessor for all environment variables, single source of truth for env config.
 */

const toBool = (value: string | undefined, defaultValue = false): boolean => {
  if (value === undefined) {
    return defaultValue;
  }
  return value.toLowerCase() === 'true';
};

export const env = {
  appName: process.env.NEXT_PUBLIC_APP_NAME ?? 'Relta',
  apiBaseUrl: process.env.NEXT_PUBLIC_API_BASE_URL ?? '',
  defaultThemeMode:
    (process.env.NEXT_PUBLIC_DEFAULT_THEME_MODE as 'light' | 'dark' | undefined) ?? 'dark',
  // Preview deployments inherit production env vars on Vercel; hard-disable
  // analytics there so preview traffic never pollutes production data.
  enableAnalytics:
    toBool(process.env.NEXT_PUBLIC_ENABLE_ANALYTICS, false) &&
    process.env.NEXT_PUBLIC_VERCEL_ENV !== 'preview',
  gaMeasurementId: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? '',
  googleAdsId: process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? '',
  // Google Ads conversion labels, one per value tier. Created in Google Ads UI and
  // pasted into .env.local / Vercel env. Missing labels silently skip the Ads conversion
  // (GA4 `generate_lead` still fires).
  googleAdsConversionLabelHigh: process.env.NEXT_PUBLIC_GADS_CONVERSION_LABEL_HIGH ?? '',
  googleAdsConversionLabelMid: process.env.NEXT_PUBLIC_GADS_CONVERSION_LABEL_MID ?? '',
  googleAdsConversionLabelSoft: process.env.NEXT_PUBLIC_GADS_CONVERSION_LABEL_SOFT ?? '',
  ga4ApiSecret: process.env.GA4_MEASUREMENT_API_SECRET ?? '',
  // Meta Pixel for Instagram/Facebook ads. Unset means no pixel loads at all.
  metaPixelId: process.env.NEXT_PUBLIC_META_PIXEL_ID ?? '',
  firstTimeBuyerGuidePdfUrl: process.env.NEXT_PUBLIC_FTB_GUIDE_PDF_URL ?? '',
  siteUrl: process.env.NEXT_PUBLIC_SITE_URL || 'https://www.relta.ca',
  // Supabase, URL needs NEXT_PUBLIC_ prefix to be available in browser
  supabaseUrl: process.env.NEXT_PUBLIC_SUPABASE_URL
    ?? (process.env.SUPABASE_PROJECT_ID ? `https://${process.env.SUPABASE_PROJECT_ID}.supabase.co` : ''),
  supabaseAnonKey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ?? '',

  // Server-only (not exposed to browser)
  fubApiKey: process.env.FUB_API_KEY ?? '',
  fubSystemKey: process.env.FUB_SYSTEM_KEY ?? '',
  supabaseSecretKey: process.env.SUPABASE_SECRET_KEY ?? '',
  // IndexNow / Bing Webmaster key, hosted at /<key>.txt for ownership verification.
  bingIndexNowKey:
    process.env.BING_WEBMASTER_INDEX_NOW_KEY ?? '142fe719b41bf3ae4c029d2cd5e9a2d9',
};

// Build-time validation for required server vars
if (typeof window === 'undefined' && process.env.NODE_ENV !== 'test') {
  if (!env.fubApiKey) {
    console.warn('[env] FUB_API_KEY is not set, CRM lead submissions will fail.');
  }
  if (!env.fubSystemKey) {
    console.warn('[env] FUB_SYSTEM_KEY is not set, CRM lead submissions will fail.');
  }
  if (!env.supabaseUrl) {
    console.warn('[env] SUPABASE_PROJECT_ID is not set, auth will not work.');
  }
  if (!env.supabaseSecretKey) {
    console.warn('[env] SUPABASE_SECRET_KEY is not set, admin auth operations will fail.');
  }
}
